import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  TableDots,
  Pagination,
  ContainerAvatar,
  enqueueSnackbar,
  marketplaceBuyModal,
  marketplaceSendOfferModal,
  TableIconsCell,
  Checkbox,
  Button,
  FlagIcon,
  shareImagesModal,
  Chip,
  SimpleTable,
  CheckboxLabel,
  containerListModal,
} from '../../design-system';
import {
  DashboardHeader,
  FilterIconButton,
  CardActions,
  Cards,
  CardsActions,
  CardsWrapper,
  CardWrapper,
} from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { momentTextFormat, showToRoles, formatContainerType, showToRolesMobile } from '../../utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useSalesQuery,
  useIsSmallScreen,
  smallScreenSize,
  clearSavedFilters,
} from '../../hooks';
import {
  TABLE,
  MESSAGES,
  imageActions,
  ContainerConditionToChipLabels,
  ContainerConditionToChipColors,
} from '../../const';
import { FeeType, SaleGetAllQuery } from '../../types';
import { containersService } from '../../services';
import { i18n } from '../../i18n';
import { Card } from '../marketplace-client-yard';
import { MobileFilters } from './MobileFilters';
import { getNotFoundSales } from './utils';

let openFilterPopup: any = () => null;
let openContainerListPopup: any = () => null;
let cancelContainerList: any = () => null;

interface MarketplacePageProps {
  innerScroll?: boolean;
}

export const MarketplacePage = styled.div<MarketplacePageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;

      @media (max-width: ${smallScreenSize}px) {
        overflow: initial;
      }
    `}

  @media (max-width: ${smallScreenSize}px) {
    grid-row-gap: var(--spacing-3);

    & > nav > * {
      justify-self: center;
    }
  }
`;

export const MarketplaceHeader = ({ totalCount, containerList, setContainerList }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();

  return (
    <DashboardHeader isSmallScreen={isSmallScreen}>
      <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
        {!isSmallScreen && i18n.t('pages.marketplace.title', { totalCount })}
        {isSmallScreen && i18n.t('pages.marketplace.shortTitle', { totalCount })}
      </Typography>
      {!isSmallScreen && containerList && (
        <Button
          size={'medium'}
          onClick={() => {
            cancelContainerList();
            setContainerList();
          }}
          data-color={'white'}
        >
          {i18n.t('button.cancel')}
        </Button>
      )}
      {!isSmallScreen && !containerList && (
        <Button size={'medium'} onClick={openContainerListPopup}>
          {i18n.t('button.containerList')}
        </Button>
      )}
      {showToRolesMobile('client', user, isSmallScreen) && (
        <FilterIconButton onClick={openFilterPopup} />
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: SaleGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const isSmallScreen = useIsSmallScreen();

  const handleClickAction = (val: any) => {
    if (val === 'buy') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter((item) => !item.isMyContainer);

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      marketplaceBuyModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.number,
        price: item.sale.finalPrice,
      }));

      marketplaceBuyModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MARKETPLACE.SUCCESS.BUY, 'success');
        refetch();
      };
      marketplaceBuyModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      <Button
        size={isSmallScreen ? 'small' : 'medium'}
        data-color={'white'}
        onClick={() => handleClickAction('buy')}
      >
        {i18n.t('button.addToCart')}
      </Button>
    </>
  );
};

export const MarketplaceBody = ({
  setData,
  setTotalCount,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  handleClickImage,
  containerList,
  setContainerList,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    country: '',
    city: '',
    yardId: '',
    clientId: '',
    number: '',
    type: 'All',
    condition: 'All',
  });
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'forSaleDate',
    sortDirection: 'desc',
  });

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useSalesQuery({
    ...filteredStates,
    user,
    filtersType: 'marketplace',
    status: 'FOR_SALE',
    take,
    skip,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    let oldContainerList = containerList;
    if (isFetching) return;

    if (!oldContainerList && typeof filteredStates.number !== 'string') {
      oldContainerList = filteredStates.number;
      setContainerList(oldContainerList);
    }
    setTotalCount(
      oldContainerList ? getNotFoundSales(data, oldContainerList).totalCount : data.totalCount,
    );
    setData((oldContainerList ? getNotFoundSales(data, oldContainerList) : data).data);
  }, [isFetching]);

  useEffect(() => {
    openFilterPopup = () => setIsOpenFilter(true);
    openContainerListPopup = () => {
      const fc = (fcProps = { containerNumber: [] }) => {
        const list = fcProps.containerNumber;
        if (!list.length) return;

        clearSavedFilters();
        setContainerList(list);
        setCheckIndexs([]);
        setStates('cancelall', {
          number: list,
        });
      };
      containerListModal.fc = fc;
      containerListModal.isFilterList = true;
      containerListModal.open();
    };
    cancelContainerList = () => {
      clearSavedFilters();
      setCheckIndexs([]);
      setStates('cancelall');
    };
  }, []);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (row: any, number: any, val: any, id: any) => {
    if (val === 'buy') {
      marketplaceBuyModal.data = [{ id, number, price: row.sale.finalPrice }];
      marketplaceBuyModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MARKETPLACE.SUCCESS.BUY, 'success');
        refetch();
      };
      marketplaceBuyModal.open();
    }
    if (val === 'send-offer') {
      marketplaceSendOfferModal.id = id;
      marketplaceSendOfferModal.number = number;
      marketplaceSendOfferModal.price = row.sale.finalPrice;
      marketplaceSendOfferModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MARKETPLACE.SUCCESS.SEND_OFFER, 'success');
        refetch();
      };
      marketplaceSendOfferModal.open();
    }
    if (val === 'personalShop') {
      window.open(`/marketplace/${row.clientYard.client.code}`, '_blank');
    }
    if (val === 'download-images') {
      containersService.downloadImages(row.images, number);
    }
    if (val === 'share-images') {
      shareImagesModal.images = row.images;
      shareImagesModal.containerNumber = number;
      shareImagesModal.open();
    }
  };

  if (showToRolesMobile('client', user, isSmallScreen))
    return (
      <>
        <CardsWrapper hasActions={showToRoles('client', user)}>
          <CardsActions>
            <CheckboxLabel
              indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
              checked={checkIndexs.length === data.data.length}
              onChange={() => handleCheckboxChange()}
              onClick={(e) => e.stopPropagation()}
              label={!checkIndexs.length ? i18n.t('label.all') : ''}
            />
            <CheckboxActions
              checkIndexs={checkIndexs.filter((i: number) => !data.data[i].isMyContainer)}
              data={data.data}
              refetch={refetch}
              setCheckIndexs={setCheckIndexs}
            />
          </CardsActions>
          <Cards>
            {data.data.map((container, i) => (
              <CardWrapper key={i}>
                <CardActions isCenter={!showToRoles('client', user)}>
                  <TableDots
                    open={openDotsMenuIndex === i}
                    onToggle={() => handleDotsToggle(i)}
                    options={[
                      ...(showToRoles('client', user) && !container.isMyContainer
                        ? [{ label: i18n.t('button.addToCart'), value: 'buy' }]
                        : []),
                      ...(showToRoles('adminRead', user)
                        ? [{ label: i18n.t('dropdown.personalShop'), value: 'personalShop' }]
                        : []),
                      ...imageActions,
                    ]}
                    onSelect={(val) => {
                      handleDotsOptionSelect(container, container.number, val, container.id);
                    }}
                  />
                  {showToRoles('client', user) && (
                    <Checkbox
                      checked={checkIndexs.includes(i)}
                      onChange={() => handleCheckboxChange(i)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </CardActions>
                <Card
                  handleClickImage={handleClickImage}
                  handleDotsOptionSelect={handleDotsOptionSelect}
                  {...container}
                />
              </CardWrapper>
            ))}
          </Cards>
        </CardsWrapper>
        <Pagination
          count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
          siblingCount={0}
          page={page}
          onChange={handleOnPageChange}
        />
        <MobileFilters
          isOpen={isOpenFilter}
          setIsOpen={setIsOpenFilter}
          setStates={handleFilterChange}
          states={states}
          {...filteredStates}
        />
      </>
    );

  return (
    <SimpleTable
      data={containerList ? getNotFoundSales(data, containerList) : data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      checkboxActions={(data) => (
        <CheckboxActions
          checkIndexs={checkIndexs.filter((i: number) => !data[i].isMyContainer)}
          data={data}
          refetch={refetch}
          setCheckIndexs={setCheckIndexs}
        />
      )}
      tableRowProps={(container, i) => ({
        key: container.number + i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {showToRoles('client', user) && (
                <Checkbox
                  indeterminate={
                    !!checkIndexs.length &&
                    checkIndexs.length !== getNotFoundSales(data, containerList).data.length
                  }
                  checked={checkIndexs.length === getNotFoundSales(data, containerList).data.length}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {i18n.t('label.actions')}
            </TableHeadCell>
          ),
          body: (container, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {showToRoles('client', user) && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <TableDots
                open={openDotsMenuIndex === i}
                onToggle={() => handleDotsToggle(i)}
                options={[
                  ...(showToRoles('client', user) && !container.isMyContainer
                    ? [{ label: i18n.t('button.addToCart'), value: 'buy' }]
                    : []),
                  ...(showToRoles('adminRead', user)
                    ? [{ label: i18n.t('dropdown.personalShop'), value: 'personalShop' }]
                    : []),
                  ...imageActions,
                ]}
                onSelect={(val) => {
                  handleDotsOptionSelect(container, container.number, val, container.id);
                }}
              />
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('label.photo'),
          body: (container) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar
                images={container.images}
                onClick={() => handleClickImage(container.images, container.id)}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (container) => container.clientYard?.client?.code,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (container) => container.number,
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (container) => formatContainerType(container.type),
        },
        {
          headText: () => i18n.t('label.condition'),
          body: (container) => (
            <TableCell align={'left'}>
              {container.condition && (
                <Chip
                  size={'small'}
                  label={ContainerConditionToChipLabels[container.condition]}
                  color={ContainerConditionToChipColors[container.condition]}
                />
              )}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.city'),
          body: (container) => (
            <TableIconsCell align={'left'} withGap>
              <FlagIcon country={container.clientYard.yard.address.country} />
              {container.clientYard.yard.address.city}
            </TableIconsCell>
          ),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'finalPrice'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.price')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            `${
              showToRoles('adminRead', user)
                ? container.sale.ownerSetPrice
                : container.sale.finalPrice
            }$`,
        },
        {
          headText: () => i18n.t('label.fee'),
          bodyText: (container) =>
            `${container.sale.feeValue}${container.sale.feeType === FeeType.FIXED ? '$' : '%'}`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.finalPrice'),
          bodyText: (container) => `${container.sale.finalPrice}$`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.buyAction'),
          body: (container) => (
            <TableCell align={'left'}>
              {!container.isMyContainer && (
                <Button
                  size={'small'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDotsOptionSelect(container, container.number, 'buy', container.id);
                  }}
                >
                  {i18n.t('button.addToCart')}
                </Button>
              )}
            </TableCell>
          ),
          isHidden: () => !showToRoles('client', user),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'forSaleDate'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.forSaleDate')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.sale.forSaleDate ? momentTextFormat(container.sale.forSaleDate, user) : '',
        },
        {
          headText: () => i18n.t('label.createdBy'),
          bodyText: (container) => container.sale.createdByName,
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
      ]}
      footer={
        containerList
          ? undefined
          : () => (
              <TableWrapperFooter>
                <Pagination
                  count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
                  page={page}
                  onChange={handleOnPageChange}
                />
              </TableWrapperFooter>
            )
      }
    />
  );
};
